import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Info, Calendar, BookOpen, Heart, Church, Users, MapPin,
  Video, Mic, Music, Mail, Phone, ChevronRight,
} from 'lucide-react';
import { API_BASE } from '../../utils/apiBase';

/**
 * InfosPccSection — section « Infos PCC » de l'accueil.
 *
 * Rend découvrables les pages Puck publiées : l'éditrice choisit dans le CMS
 * si une page apparaît (showInNav), son libellé, son icône et son ordre. On
 * lit le résumé public `GET /api/pages-menu` (slug/label/icon/order, jamais le
 * contenu) et on affiche des tuiles → `/page/:slug`. Ne rend RIEN s'il n'y a
 * aucune page à afficher (pas de section vide).
 */

// Jeu d'icônes fixe proposé à l'éditrice (clé stockée dans navIcon).
const NAV_ICONS = {
  info: Info,
  calendar: Calendar,
  book: BookOpen,
  heart: Heart,
  church: Church,
  users: Users,
  map: MapPin,
  video: Video,
  mic: Mic,
  music: Music,
  mail: Mail,
  phone: Phone,
};

export default function InfosPccSection({ props = {} } = {}) {
  const sectionTitle = props.title || 'Infos PCC';
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/pages-menu`, { headers: { Accept: 'application/json' } });
        if (!res.ok) return;
        const data = await res.json();
        // Défense : la page `accueil` EST l'écran d'accueil, jamais une tuile
        // « Infos PCC » (le backend l'exclut déjà ; on double le garde-fou).
        if (alive && Array.isArray(data?.items)) {
          setItems(data.items.filter((it) => (it.slug || '').toLowerCase() !== 'accueil'));
        }
      } catch (_) { /* silencieux : la section disparaît simplement */ }
    })();
    return () => { alive = false; };
  }, []);

  if (!items.length) return null;

  return (
    <div className="py-6 bg-pcc-bg" data-testid="infos-pcc-section">
      <div className="flex items-center gap-2 px-4 mb-4">
        <Info size={20} className="text-[#9398DE]" />
        <h2 className="text-lg font-bold text-pcc-text">{sectionTitle}</h2>
      </div>
      <div className="px-4 grid grid-cols-2 gap-3">
        {items.map((it) => {
          const Icon = NAV_ICONS[it.icon] || Info;
          return (
            <button
              key={it.slug}
              onClick={() => navigate(`/page/${it.slug}`)}
              className="flex items-center gap-3 rounded-2xl p-3.5 text-left shadow-sm transition-all hover:shadow-md bg-pcc-card"
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-none bg-[#9398DE]/10">
                <Icon size={20} className="text-[#9398DE]" />
              </div>
              <span className="flex-1 min-w-0 font-semibold text-pcc-text truncate" style={{ fontSize: '14px' }}>
                {it.label}
              </span>
              <ChevronRight size={16} className="text-pcc-text-muted flex-none" />
            </button>
          );
        })}
      </div>
    </div>
  );
}
